import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from '@mui/material/Tooltip';
import { getInitials } from "../../utilities/helpers";

export default function ImportEmployeesModal({
  open,
  onClose,
  posEmployees = [],
  storeTeamMemberLookup = [],
  import_store_team_members_from_pos,
  link_pos_store_team_member,
  archive_pos_store_team_member
}) {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [employeeToDelete, setEmployeeToDelete] = React.useState(null);
  const [linkModalOpen, setLinkModalOpen] = React.useState(false);
  const [employeeToLink, setEmployeeToLink] = React.useState(null);
  const [selectedLinkEmployee, setSelectedLinkEmployee] = React.useState(null);

  // Get potential matches from the employee's best_matches array and storeTeamMemberLookup
  const getPotentialMatches = (employee) => {
    if (!employee.best_matches || !Array.isArray(employee.best_matches)) {
      return [];
    }

    return employee.best_matches.map(([id, similarity_score]) => {
      // Find the corresponding team member in the lookup array
      const teamMember = storeTeamMemberLookup.find(member => member.id === id);

      if (!teamMember) {
        return null;
      }

      // Parse the full_name to get first and last name
      const nameParts = teamMember.full_name.split(' ');
      const first_name = nameParts[0] || '';
      const last_name = nameParts.slice(1).join(' ') || '';

      return {
        id: teamMember.id,
        first_name,
        last_name,
        full_name: teamMember.full_name,
        email: teamMember.email || '', // Add email if available in your lookup data
        similarity_score: Math.round(similarity_score * 100)
      };
    }).filter(Boolean); // Remove null entries
  };

  const handleClose = () => {
    onClose();
  };

  const handleImportEmployee = (employeeUuid) => {
    import_store_team_members_from_pos(employeeUuid);
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setDeleteModalOpen(true);
  };

  console.log("EMPLOYEE TO DELETE", employeeToDelete)

  const handleDeleteConfirm = () => {
    archive_pos_store_team_member(employeeToDelete.uuid)
    setDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  const handleLinkClick = (employee) => {
    setEmployeeToLink(employee);
    setLinkModalOpen(true);
    setSelectedLinkEmployee(null);
  };

  const handleLinkCancel = () => {
    setLinkModalOpen(false);
    setEmployeeToLink(null);
    setSelectedLinkEmployee(null);
  };

  const handleLinkConfirm = () => {
    if (selectedLinkEmployee && employeeToLink) {
      // Call the API to link the POS employee to the existing team member
      link_pos_store_team_member(employeeToLink.uuid, selectedLinkEmployee.id);
    }
    setLinkModalOpen(false);
    setEmployeeToLink(null);
    setSelectedLinkEmployee(null);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    maxHeight: "95vh",
    overflow: "auto",
  };

  const deleteModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
  };

  const linkModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    maxHeight: "95vh",
    overflow: "auto",
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="pos-employees-modal-title"
        aria-describedby="pos-employees-modal-description"
      >
        <Box sx={modalStyle}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-6 flex items-center space-x-3">
              <WarningIcon className="text-amber-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                Employees from Point of Sale System
              </h3>
            </div>

            <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-800 text-sm">
                The following employees exist in your point of sale system but haven't been imported yet.
                Click the plus icon to import each employee.
              </p>
            </div>

            <div className="text-sm font-medium text-gray-700 mb-2">
              {posEmployees.length} employees
            </div>

            {posEmployees && posEmployees.length > 0 && (
              <div className="mb-4">
                <div className="max-h-[450px] overflow-y-auto border border-gray-200 rounded-lg">
                  {posEmployees.map((employee) => (
                    <div
                      key={employee.uuid}
                      className="flex items-center px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                    >
                      <Tooltip title="Add to Rightwork" placement="top">
                        <IconButton
                          onClick={() => handleImportEmployee(employee.uuid)}
                          size="small"
                          className="text-gray-400 hover:text-emerald-600 transition-colors"
                          sx={{
                            '&:hover': {
                              color: '#059669',
                              backgroundColor: '#f3f4f6',
                            }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">
                            {getInitials(employee.first_name, employee.last_name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {employee.first_name} {employee.last_name}
                            </p>
                            {employee.email && (
                              <p className="text-xs text-gray-500">{employee.email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Tooltip title="Link to existing account in Rightwork" placement="top">
                          <IconButton
                            onClick={() => handleLinkClick(employee)}
                            size="small"
                            className="text-gray-400 hover:text-violet-600 transition-colors"
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Exclude from Rightwork" placement="top">
                          <IconButton
                            onClick={() => handleDeleteClick(employee)}
                            size="small"
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            sx={{
                              '&:hover': {
                                color: '#ef4444',
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <button
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-employee-modal-title"
        aria-describedby="delete-employee-modal-description"
      >
        <Box sx={deleteModalStyle}>
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <DeleteIcon className="text-red-600" />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Exclude Employee from Rightwork
            </h3>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to exclude this employee from the import employees list?
              This action can't be undone.
            </p>

            {employeeToDelete && (
              <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">
                    {getInitials(employeeToDelete.first_name, employeeToDelete.last_name)}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                      {employeeToDelete.first_name} {employeeToDelete.last_name}
                    </p>
                    {employeeToDelete.email && (
                      <p className="text-xs text-gray-500">{employeeToDelete.email}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 text-sm font-medium"
                onClick={handleDeleteConfirm}
              >
                Exclude
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Link Employee Modal */}
      <Modal
        open={linkModalOpen}
        onClose={handleLinkCancel}
        aria-labelledby="link-employee-modal-title"
        aria-describedby="link-employee-modal-description"
      >
        <Box sx={linkModalStyle}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={handleLinkCancel}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-6 flex items-center space-x-3">
              <LinkIcon className="text-violet-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                Link Employee Account
              </h3>
            </div>

            {employeeToLink && (
              <>
                <div className="mb-6 p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <p className="text-violet-700 text-sm mb-3">
                    Link this POS employee to an existing team member in Rightwork:
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">
                      {getInitials(employeeToLink.first_name, employeeToLink.last_name)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {employeeToLink.first_name} {employeeToLink.last_name}
                      </p>
                      {employeeToLink.email && (
                        <p className="text-xs text-gray-600">{employeeToLink.email}</p>
                      )}
                      <p className="text-xs text-violet-600 font-medium">From POS System</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Potential Matches in Rightwork
                  </h4>
                  <div className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg">
                    {getPotentialMatches(employeeToLink).map((match) => (
                      <div
                        key={match.id}
                        className={`flex items-center px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors
                          ${selectedLinkEmployee?.id === match.id
                            ? 'bg-violet-50 border-violet-200'
                            : 'hover:bg-gray-50'}`}
                        onClick={() => setSelectedLinkEmployee(match)}
                      >
                        <div className="flex items-center flex-1 space-x-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">
                            {getInitials(match.first_name, match.last_name)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {match.full_name}
                                </p>
                                {match.email && (
                                  <p className="text-xs text-gray-500">{match.email}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                                  ${match.similarity_score >= 90
                                    ? 'bg-green-100 text-green-800'
                                    : match.similarity_score >= 75
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'}`}>
                                  {match.similarity_score}% match
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                            ${selectedLinkEmployee?.id === match.id
                              ? 'border-violet-600 bg-violet-600'
                              : 'border-gray-300'}`}>
                            {selectedLinkEmployee?.id === match.id && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                    onClick={handleLinkCancel}
                  >
                    Cancel
                  </button>
                  <button
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                      ${selectedLinkEmployee
                        ? 'bg-violet-600 text-white hover:bg-violet-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                    onClick={() => selectedLinkEmployee && handleLinkConfirm()}
                    disabled={!selectedLinkEmployee}
                  >
                    Link Employee
                  </button>
                </div>
              </>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}
